import { useEffect, useState, useMemo } from "react";
import { Checkbox } from "antd";
import { LabelledSelect } from "../../../../Fields";
import { ToggleButton, DecimalInput, Input2 } from "components";
import { useInput } from "hooks";
import AutocompleteFreeText from "components/AutocompleteFreeText";
import useAdBlockStore from 'store/AdBlockStore';
import { getStoreSavingsIncentivePredictiveText, STORE_SAVINGS_INCENTIVE_PREDICTIVE_FREE_TEXT } from "config";
import { formatDollarValue } from "utils";

export const IncentiveStoreSavingsTextBuilder = ({
  value = "",
  onChange = () => { },
}) => {
  const isUpTo = useInput(false);
  const start = useInput(1.0);
  const end = useInput();
  const type = useInput("$");
  const withCashText = useInput("");
  const isPurchase = useInput(false);
  const purchaseValStart = useInput("");
  const purchaseValEnd = useInput("");
  const purchaseType = useInput("$")
  const isCashAvailable = useInput(false);
  const suffix = useInput("");

  const typeData = [{ label: "$", value: "$" }, { label: "%", value: "%" }, { label: "X", value: "X" }];
  const purchaseTypeData = [{ label: "$", value: "$" }, { label: "Unit", value: "Unit" }];

  const [incentiveText, setIncentiveText] = useState(value)
  const activeBlockData = useAdBlockStore(state => state.activeBlockData);

  const storeSavingsPredictiveText = useMemo(
    () => getStoreSavingsIncentivePredictiveText(activeBlockData?.retailer_name),
    [activeBlockData?.retailer_name]
  );

  const formatFirstLine = (start, end, type) => {
    let result = "";
    if (type === "$" && start) {
      result = "$" + formatDollarValue(start) + (end ? `-$${formatDollarValue(end)}` : "");
    } else if (type === "%" && start) {
      result = Math.trunc(start.replace("%", "")) + "%" + (end ? `-${Math.trunc(end.replace("%", ""))}%` : "");
    } else if (type === "X" && start) {
      result = Math.trunc(start.replace("X", "")) + "X" + (end ? `-${Math.trunc(end.replace("X", ""))}X` : "");
    }
    return result ? ` ${result}` : "";
  };

  const formatPurchase = (isP, pVStart, pVEnd, pT) => {
    let result = "";
    if (isP) {
      result = " w/purchase/";
      if (pT === "$" && pVStart) {
        result += `$${formatDollarValue(pVStart)}${pVEnd ? `-$${formatDollarValue(pVEnd)}` : ""}`;
      } else if (pT === "Unit" && pVStart) {
        result += `${Math.trunc(pVStart)}${pVEnd ? `-${Math.trunc(pVEnd)}` : ""}`;
      }
    }
    return result;
  };

  const getCashAvailableText = (text = "") => {
    const normalizedText = text.trim().replace(/^w\s*\/\s*/i, "").trim();
    if (!/cash/i.test(normalizedText)) {
      return "";
    }
    return `${normalizedText} Available`;
  };

  const updateIncentiveText = (nextText = "") => {
    setIncentiveText(nextText);
    onChange(nextText);
  };

  const handleUpdateValue = (isUpto, start, end, type, isPurchase, purchaseType, purchaseValStart, purchaseValEnd, withCashText, suffix, isCA = false) => {
    if (isCA) {
      updateIncentiveText(getCashAvailableText(withCashText));
    } else {
      updateIncentiveText(
        `Earn${isUpto ? " Up To" : ""}${formatFirstLine(start, end, type)}${withCashText ? ` ${withCashText}` : ""}${formatPurchase(isPurchase, purchaseValStart, purchaseValEnd, purchaseType)}${suffix ? ` ${suffix}` : ""}`
      );
    }
  };

  useEffect(() => {
    let main = value.trim();
    isCashAvailable.setValue(false);
    if (main.toLowerCase().endsWith(" available")) {
      const selectedCashText = main.slice(0, -" available".length).trim();
      if (/cash/i.test(selectedCashText)) {
        withCashText.setValue(`w/${selectedCashText}`);
        isCashAvailable.setValue(true);
        return;
      }
    }
    if (main.startsWith("Earn")) {
      if (main.startsWith("Earn Up To")) {
        isUpTo.setValue(true);
        main = main.slice(10).trim();
      } else {
        isUpTo.setValue(false);
        main = main.slice(4).trim();
      }
      const splits = main.split(" ");
      if (splits.length > 0) {
        if (splits[0].startsWith("$")) {
          type.setValue("$");
          const parts = splits[0].split("-");
          if (parts.length > 1) {
            start.setValue(parts[0].replace("$", "").trim());
            end.setValue(parts[1].replace("$", "").trim());
          } else {
            start.setValue(splits[0].replace("$", "").trim());
          }
          main = splits.slice(1).join(" ");
        } else {
          type.setValue(splits[0].endsWith("%") ? "%" : splits[0].endsWith("X") ? "X" : "");
          const parts = splits[0].split("-");
          if (parts.length > 1) {
            start.setValue(parts[0].replace("$", "").replace("X", "").replace("%", "").trim());
            end.setValue(parts[1].replace("$", "").replace("X", "").replace("%", "").trim());
          } else {
            start.setValue(splits[0].replace("$", "").replace("X", "").replace("%", "").trim());
          }
          main = splits.slice(2).join(" ");
        }
      }
      const pIdx = main.indexOf("w/purchase/");
      if (pIdx >= 0) {
        withCashText.setValue(main.slice(0, pIdx).trim());
        isPurchase.setValue(true);
        const purchaseAndSuffix = main.slice(pIdx + 11);
        const suffixIdx = purchaseAndSuffix.indexOf(" ");
        const purchaseStr = suffixIdx >= 0
          ? purchaseAndSuffix.slice(0, suffixIdx).trim()
          : purchaseAndSuffix;
        suffix.setValue(
          suffixIdx >= 0 ? purchaseAndSuffix.slice(suffixIdx + 1).trim() : ""
        );
        if (purchaseStr.startsWith("$")) {
          purchaseType.setValue("$");
          const inner = purchaseStr.slice(1);
          const dashIdx = inner.indexOf("-$");
          if (dashIdx >= 0) {
            purchaseValStart.setValue(inner.slice(0, dashIdx).trim());
            purchaseValEnd.setValue(inner.slice(dashIdx + 2).trim());
          } else {
            purchaseValStart.setValue(inner.trim());
            purchaseValEnd.setValue("");
          }
        } else {
          purchaseType.setValue("Unit");
          const parts = purchaseStr.split("-");
          purchaseValStart.setValue(parts[0].trim());
          if (parts.length > 1) {
            purchaseValEnd.setValue(parts[1].trim());
          } else {
            purchaseValEnd.setValue("");
          }
        }
      } else {
        isPurchase.setValue(false);
        purchaseValStart.setValue("");
        purchaseValEnd.setValue("");
        // Split main into withCashText (dropdown value) and suffix (free text)
        // by matching the longest known dropdown option at the start of main
        const mainTrimmed = main.trim();
        const sortedOptions = [...storeSavingsPredictiveText].sort(
          (a, b) => (b.id || "").length - (a.id || "").length
        );
        let detectedCashText = "";
        let detectedSuffix = "";
        for (const option of sortedOptions) {
          if (option.id && mainTrimmed.startsWith(option.id)) {
            detectedCashText = option.id;
            detectedSuffix = mainTrimmed.slice(option.id.length).trim();
            break;
          }
        }
        withCashText.setValue(detectedCashText || mainTrimmed);
        suffix.setValue(detectedCashText ? detectedSuffix : "");
      }
    }

    return () => { };
  }, [value, storeSavingsPredictiveText]);

  return (
    <>
      <div className="p-2 d-flex justify-content-between align-items-center">
        <Checkbox
          checked={isCashAvailable.value && !!getCashAvailableText(withCashText.value)}
          disabled={!getCashAvailableText(withCashText.value)}
          onChange={(e) => {
            isCashAvailable.setValue(e.target.checked);
            handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, e.target.checked);
          }}
        >
          {getCashAvailableText(withCashText.value)}
        </Checkbox>
      </div>
      <div className="p-2 d-flex justify-content-between align-items-center">
        <div className="d-flex mt-2" style={{ width: '100%' }}>
          <div className="mr-2">Earn</div>
          <Checkbox checked={isUpTo.value}
            disabled={isCashAvailable.value}
            onChange={(e) => {
              isUpTo.setValue(e.target.checked);
              handleUpdateValue(e.target.checked, start.value, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
            }}
          >
            Up To
          </Checkbox>
        </div>

        {type.value === '$' ? (
          <DecimalInput labeled
            label="Start Val"
            value={start.value}
            disabled={isCashAvailable.value}
            onChange={(e) => {
              start.setValue(e);
              handleUpdateValue(
                isUpTo.value, e, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
            }}
          />
        ) : (
          <Input2
            label="Start Val"
            value={start.value}
            type="int"
            disabled={isCashAvailable.value}
            onChange={(e) => {
              start.setValue(e);
              handleUpdateValue(
                isUpTo.value, e, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
            }}
          />
        )}


        <div className="mt-auto mb-1 p-2 text">-</div>

        {type.value === '$' ? (
          <DecimalInput labeled
            label="End Val"
            value={end.value}
            disabled={isCashAvailable.value}
            onChange={(e) => {
              end.setValue(e);
              handleUpdateValue(isUpTo.value, start.value, e, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
            }}
          />
        ) : (
          <Input2
            label="End Val"
            value={end.value}
            type="int"
            disabled={isCashAvailable.value}
            onChange={(e) => {
              end.setValue(e);
              handleUpdateValue(
                isUpTo.value, start.value, e, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
            }}
          />
        )}

        <LabelledSelect label="Type" className="ml-2" style={{ width: '100%' }}
          options={typeData} value={type.value}
          disabled={isCashAvailable.value}
          onChange={(e) => {
            const nextStartValue = e === '$' ? formatDollarValue(start.value) : start.value;
            const nextEndValue = e === '$' ? formatDollarValue(end.value) : end.value;

            type.setValue(e);
            start.setValue(nextStartValue);
            end.setValue(nextEndValue);
            handleUpdateValue(isUpTo.value, nextStartValue, nextEndValue, e, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
          }} />
      </div>
      <div className="p-2 d-flex justify-content-between align-items-center">
        <LabelledSelect label="" className="ml-2" style={{ width: '100%' }}
          fieldNames={{ label: 'title', value: 'id' }}
          options={storeSavingsPredictiveText} value={withCashText.value}
          disabled={isCashAvailable.value}
          onChange={(e) => {
            withCashText.setValue(e);
            handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, e, suffix.value, isCashAvailable.value);
          }} />
      </div>
      <div className="p-2 d-flex justify-content-between align-items-center">
        <ToggleButton className="mt-3" label="w/purchase" value={isPurchase.value}
          disabled={isCashAvailable.value}
          onChange={(e) => {
            isPurchase.setValue(e);
            handleUpdateValue(isUpTo.value, start.value, end.value, type.value, e, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
          }}
        />

        <LabelledSelect label="Type" className="ml-2" style={{ width: '85%' }}
          options={purchaseTypeData} value={purchaseType.value}
          disabled={isCashAvailable.value}
          onChange={(e) => {
            const nextPurchaseValStart = e === '$' ? formatDollarValue(purchaseValStart.value) : purchaseValStart.value;
            const nextPurchaseValEnd = e === '$' ? formatDollarValue(purchaseValEnd.value) : purchaseValEnd.value;

            purchaseType.setValue(e);
            purchaseValStart.setValue(nextPurchaseValStart);
            purchaseValEnd.setValue(nextPurchaseValEnd);
            handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, e, nextPurchaseValStart, nextPurchaseValEnd, withCashText.value, suffix.value, isCashAvailable.value);
          }} />

        {purchaseType.value === '$' ? (
          <>
            <DecimalInput className="" labeled
              label="Start Val"
              value={purchaseValStart.value}
              disabled={!isPurchase.value || isCashAvailable.value}
              onChange={(e) => {
                purchaseValStart.setValue(e);
                handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, purchaseType.value, e, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
              }}
            />

            <DecimalInput className="ml-2" labeled
              label="End Val"
              value={purchaseValEnd.value}
              disabled={!isPurchase.value || isCashAvailable.value}
              onChange={(e) => {
                purchaseValEnd.setValue(e);
                handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, e, withCashText.value, suffix.value, isCashAvailable.value);
              }}
            />
          </>
        ) : (
          <>
            <Input2
              label="Start Val"
              value={purchaseValStart.value}
              type="int"
              disabled={!isPurchase.value || isCashAvailable.value}
              onChange={(e) => {
                purchaseValStart.setValue(e);
                handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, purchaseType.value, e, purchaseValEnd.value, withCashText.value, suffix.value, isCashAvailable.value);
              }}
            />
            <Input2
              label="End Val"
              value={purchaseValEnd.value}
              type="int"
              disabled={!isPurchase.value || isCashAvailable.value}
              onChange={(e) => {
                purchaseValEnd.setValue(e);
                handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, e, withCashText.value, suffix.value, isCashAvailable.value);
              }}
            />
          </>
        )}

      </div>
      <div className="p-2 d-flex justify-content-between align-items-center">
        <AutocompleteFreeText
          label="Suffix"
          value={suffix.value}
          data={STORE_SAVINGS_INCENTIVE_PREDICTIVE_FREE_TEXT}
          disabled={isCashAvailable.value}
          onChange={(e) => {
            suffix.setValue(e);
            handleUpdateValue(isUpTo.value, start.value, end.value, type.value, isPurchase.value, purchaseType.value, purchaseValStart.value, purchaseValEnd.value, withCashText.value, e, isCashAvailable.value);
          }}
        />
      </div>
    </>
  );
};